import { IconMessage } from '@tabler/icons-react-native';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useUser } from 'app/network/user';
import { SUPPORT_CHANNEL_URL, SUPPORT_TRANSPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { useCreateOneMessage } from 'app/network/chat/message';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { createTheme } from 'app/shopify';
import { MessageKind } from 'app/types/chat';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, getShareListingUrl, isFeatureEnabled } from 'app/utils/helpers';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

const theme = createTheme();

export function TransportMessage({ listingId }: { listingId?: string }) {
  const navigation = useNavigation();
  const { t } = useTranslation('one-channel');
  const { data: currentUser, isPending: isUserLoading } = useUser();
  const { isPending: isCreatingMessage, mutate: createMessage } = useCreateOneMessage();

  const canUseHelpToTransport =
    !isUserLoading && isFeatureEnabled(FeatureFlag.helpToTransport, currentUser?.featureFlags);

  const onPress = () => {
    const targetChannelUrl = canUseHelpToTransport ? SUPPORT_TRANSPORT_CHANNEL_URL : SUPPORT_CHANNEL_URL;

    if (!listingId) {
      navigateToChat(navigation, targetChannelUrl);
      return;
    }

    createMessage(
      {
        channel_url: targetChannelUrl,
        message: getShareListingUrl(listingId),
        type: MessageKind.Message,
        isFirstMessage: false,
      },
      {
        onSuccess: ({ channel }) => navigateToChat(navigation, channel.id),
      },
    );
  };

  return (
    <Column alignItems="center" gap="spacing.general.x4" style={styles.card} width="100%">
      <Row style={styles.iconPill}>
        <Text fontSize={20}>🚚</Text>
      </Row>
      <Text fontSize={16} fontWeight="700" textAlign="center">
        {t('transportPrompt.title')}
      </Text>
      <Text fontSize={14}>{t('transportPrompt.description')}</Text>
      <Button
        loading={isCreatingMessage}
        onPress={onPress}
        prefix={<IconMessage color={theme.colors['text.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />}
        style={styles.button}
        textAlign="center"
        variant="secondary"
        width="100%"
      >
        {t('transportPrompt.cta')}
      </Button>
    </Column>
  );
}

const styles = StyleSheet.create({
  button: {
    minHeight: 48,
  },
  card: {
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: 16,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x4'],
  },
  iconPill: {
    backgroundColor: theme.colors['surface.primary'],
    borderRadius: 35,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x2'],
  },
});
